import { memo } from "react";
import { Link } from "@tanstack/react-router";
import { ShoppingCart } from "@medusajs/icons";
import { useSellerCarts } from "@/queries/cart";
import { cn } from "@repo/design-system/utils";
import { CartContents } from "./CartContents";
import { CartSummaryStats } from "./CartSummaryStats";
import { SellerCartAccordion } from "./SellerCartAccordion";

export interface CartSidebarProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Floating cart sidebar for the search page.
 *
 * Features:
 * - Auto-hides when cart is empty
 * - Rail only at `xl`; below that the same cart opens as a slide-over from the
 *   header (`CartDrawer`), or is reached from the phone sticky bar
 * - Accordion-style display with one seller cart expanded at a time
 * - Shows summary stats at the top
 */
export const CartSidebar = memo(function CartSidebar({
  className
}: CartSidebarProps) {
  const { data, isLoading } = useSellerCarts();

  // Auto-hide when cart is empty
  const hasItems = data && data.summary.total_items > 0;

  if (!isLoading && !hasItems) {
    return null;
  }

  return (
    <aside
      className={cn(
        // The rail is a `2xl`-only pane. Below that the cart leaves the
        // layout and becomes the header slide-over, handing ~320px back to the
        // results - see storefront#145 (P4).
        "hidden 2xl:flex",
        // Positioning and sizing
        "w-80 flex-col",
        "h-fit max-h-[calc(100vh-8rem)]",
        // Styling
        "rounded-lg border border-gray-200 bg-white shadow-sm",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-purple-600" />
          <span className="font-semibold text-gray-900">Cart</span>
        </div>
        <Link
          to="/cart"
          className="text-xs font-medium text-purple-600 hover:text-purple-700"
        >
          View Full Cart
        </Link>
      </div>

      <CartContents className="flex-1 overflow-y-auto" />

      {/* Footer - Checkout button. Only once there is something to check out;
          the empty state is a transient here (the aside hides itself), but the
          shared body renders it while the query is still resolving. */}
      {hasItems && (
        <div className="border-t border-gray-100 p-4">
          <Link
            to="/cart"
            className={cn(
              "flex w-full items-center justify-center",
              "rounded-lg bg-purple-600 px-4 py-2.5",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700"
            )}
          >
            Proceed to Checkout
          </Link>
        </div>
      )}
    </aside>
  );
});

export { CartContents, type CartContentsProps } from "./CartContents";
export {
  CartSummaryStats,
  type CartSummaryStatsProps
} from "./CartSummaryStats";
export {
  SellerCartAccordion,
  type SellerCartAccordionProps
} from "./SellerCartAccordion";
